//DashboardFrame.js
import React, { useEffect, useState } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { RadioGroup, Radio, FormControl } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import LabFilingDetails from "./LabFilingDetails";

import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Autocomplete,
  TextField,
  Checkbox,
  FormGroup,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTableOne,
  fetchTableTwo,
  fetchTableThree,
  fetchDayData,
  fetchDayByCategory,
  fetchDateRangeExcel,
  setLabReport,
  fetchFiling,
  setSelectedDate,
  setSelectedDay,
  toggleLiverun,
  resetDashboard,
} from "../reducers/dashFrameSlice";
import {
  GridColumnMenu,
  GridColumnMenuSortItem,
  GridColumnMenuFilterItem,
} from "@mui/x-data-grid";

function CustomColumnMenu(props) {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuColumnsItem: null, // Removes "Manage columns"
        columnMenuHideItem: null, // Removes "Hide column"
      }}
    />
  );
}

const DASHBOARDCOLORS = {
  dark_boarder: "#7c6fe0",
};

const COLORS = {
  filed: "#81c784",
  reviewed_and_actioned: "#ffb74d",
  not_filed_inconclusive: "#e57373",
  not_filed_setup: "#64b5f6",
  not_filed_already: "#ba68c8",
  error: "#ff5252",
};

const BCOLORS = {
  filed: "#388e3c",
  reviewed_and_actioned: "#f57c00",
  not_filed_inconclusive: "#c62828",
  not_filed_setup: "#1565c0",
  not_filed_already: "#6a1b9a",
  error: "#d32f2f",
};

const COLOR_TITLES = {
  filed: "Filed",
  reviewed_and_actioned: "Out of Range",
  not_filed_inconclusive: "Not Filed (Inconclusive Report)",
  not_filed_setup: "Category Not Setup",
  not_filed_already: "Not Filed (Already filed by clinician)",
  error: "Error",
};

const TABLE_THREE_COLUMNS = [
  {
    field: "output_date",
    headerName: "Date",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "enabled_received",
    flex: 1,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Total Path
        <br />
        Reports Reviewed
      </div>
    ),
    cellClassName: "bg-grey",
  },
  {
    field: "filed_normal_count",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Total Filed
        <br />
        Normal
      </div>
    ),
  },
  {
    field: "filed_abnormal_count",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Total Filed
        <br />
        Abnormal
      </div>
    ),
  },
  {
    field: "reviewed_out_of_range_count",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Total Reviewed
        <br />
        (Out Of Range)
      </div>
    ),
  },
  {
    field: "not_filed_already_filed_by_clinician_count",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Not Filed -
        <br />
        Filed by Clinician
      </div>
    ),
  },
  {
    field: "not_filed_inconclusive_report_count",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Not Filed -
        <br />
        Inconclusive Report
      </div>
    ),
  },
  {
    field: "not_filed_rules_not_setup_count",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Total Path Reports for
        <br />
        Not Enabled Categories
      </div>
    ),
  },
];

const TABLE_DAYDATA_COLUMNS = [
  {
    field: "nhs_num",
    headerName: "NHS Number",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        NHS
        <br />
        Number
      </div>
    ),
  },
  {
    field: "emis_id",
    headerName: "EMIS ID",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "lab_report_id",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Lab Report
        <br />
        Id
      </div>
    ),
  },
  {
    field: "category",
    headerName: "Category",
    flex: 3,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "category_type",
    headerName: "Report Type",
    flex: 2,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",

    renderCell: (params) =>
      params.value ? "Normal Report" : "Abnormal Report",
  },
  {
    field: "status",
    headerName: "Status",
    flex: 2.5,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "ordering_party",
    headerName: "Ordering Party",
    flex: 1.5,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "usual_gp",
    headerName: "Usual GP",
    flex: 1.5,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "notes",
    headerName: "Notes",
    flex: 3,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
    valueGetter: (params) => {
      const row = params.row;
      const base = row?.notes ? String(row.notes) : "";
      if (row?.report_has_review !== true) return base;
      const reviewNote = "Filing has an approved review and approve record.";
      return base ? `${base} | ${reviewNote}` : reviewNote;
    },
  },
];

const PIE_COLORS = [
  "#81c784",
  "#4db6ac",
  "#ffd54f",
  "#9575cd",
  "#7986cb",
  "#a1887f",
  "#f06292",
  "#64b5f6",
  "#e57373",
  "#ba68c8",
];

const CustomBar = (props) => {
  const { x, y, width, height, fill, stroke, strokeWidth, isSelected } = props;
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      stroke={isSelected ? DASHBOARDCOLORS.dark_boarder : stroke}
      strokeWidth={isSelected ? 3 : strokeWidth}
    />
  );
};

const DashboardFrame = () => {
  const dispatch = useDispatch();
  const {
    liverun,
    tableOne,
    tableOneTotalProcessed,
    tableOneTotalNormal,
    tableOneTotalAbnormal,
    tableOneInreviewCategories,
    tableOneEnabledCategories,
    tableOneMonthlyTrend,
    lab,
    tableTwo,
    tableThree,
    tableDayByCategory,
    tableDayData,
    selectedDate,
    selectedDay,
    loadingOne,
    loadingTwo,
    loadingThree,
    loadingDayByCategory,
    loadingDayData,
    loadingLabReport,
  } = useSelector((state) => state.dashFrame);

  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  const access_token = useSelector((state) => state.authen.accessToken);
  const is_admin = Boolean(access_token);

  const TABLE_CATEGORY_COLUMNS = [
    {
      field: "category",
      headerName: "Category",
      flex: 2.5,
      align: "center",
      headerAlign: "center",
      cellClassName: "bg-grey",
    },
    {
      field: "filed",
      renderHeader: () => (
        <div style={{ textAlign: "center", whiteSpace: "normal" }}>
          Total Path
          <br />
          Reports Filed
        </div>
      ),
      flex: 1,
      align: "center",
      headerAlign: "center",
      cellClassName: "bg-grey",
    },
    {
      field: "reviewed_and_actioned",
      renderHeader: () => (
        <div style={{ textAlign: "center", whiteSpace: "normal" }}>
          Total Reviewed
          <br />
          (Out of Range)
        </div>
      ),
      flex: 1,
      align: "center",
      headerAlign: "center",
      cellClassName: "bg-grey",
    },
    {
      field: "not_filed_already",
      renderHeader: () => (
        <div style={{ textAlign: "center", whiteSpace: "normal" }}>
          Not Filed -
          <br />
          Filed by Clinician
        </div>
      ),
      flex: 1,
      align: "center",
      headerAlign: "center",
      cellClassName: "bg-grey",
    },
    {
      field: "not_filed_inconclusive",
      renderHeader: () => (
        <div style={{ textAlign: "center", whiteSpace: "normal" }}>
          Not Filed -
          <br />
          Inconclusive Report
        </div>
      ),
      flex: 1,
      align: "center",
      headerAlign: "center",
      cellClassName: "bg-grey",
    },
    {
      field: "not_filed_setup",
      renderHeader: () => (
        <div style={{ textAlign: "center", whiteSpace: "normal" }}>
          Total Path Reports for
          <br />
          Not Enabled Categories
        </div>
      ),
      flex: 1,
      align: "center",
      headerAlign: "center",
      cellClassName: "bg-grey",
    },
    ...(is_admin
      ? [
          {
            field: "error",
            headerName: "Error",
            flex: 1,
            align: "center",
            headerAlign: "center",
            cellClassName: "bg-grey",
          },
        ]
      : []),
  ];

  const [visibleKeys, setVisibleKeys] = useState({
    filed: true,
    reviewed_and_actioned: true,
    not_filed_inconclusive: false,
    not_filed_setup: false,
    not_filed_already: false,
    error: false,
  });

  const [openFilingDialog, setOpenFilingDialog] = useState(false);

  const [page, setPage] = useState(0);
  const [page3, setPage3] = useState(0);
  const [page4, setPage4] = useState(0);
  const [selectedChartType, setSelectedChartType] = useState("filed");

  const [selectedDayRowId, setSelectedDayRowId] = useState(null);
  const [selectedEMISId, setSelectedEMISId] = useState(null);
  const [selectedCategoryRowId, setSelectedCategoryRowId] = useState(null);

  const [dayDataFilters, setDayDataFilters] = useState({
    nhs_num: "",
    emis_id: "",
    lab_report_id: "",
    categories: [],
    ordering_parties: [],
    usual_gps: [],
    statuses: [],
    category_type: "",
    report_has_review: "",
  });

  const [rangeDialogOpen, setRangeDialogOpen] = useState(false);
  const [rangeStartDate, setRangeStartDate] = useState("");
  const [rangeEndDate, setRangeEndDate] = useState("");
  const [rangeDownloading, setRangeDownloading] = useState(false);
  const [rangeError, setRangeError] = useState(null);

  const dayDataStatusOptions = React.useMemo(() => {
    const set = new Set(tableDayData.map((r) => r.status).filter(Boolean));
    return Array.from(set).sort();
  }, [tableDayData]);

  const dayDataCategoryOptions = React.useMemo(() => {
    const set = new Set();
    tableDayData.forEach((r) => {
      String(r.category ?? "")
        .split(",")
        .map((c) => c.trim())
        .filter(Boolean)
        .forEach((c) => set.add(c));
    });
    return Array.from(set)
      .sort()
      .map((cat) => ({ label: cat, value: cat }));
  }, [tableDayData]);

  const dayDataOrderingPartyOptions = React.useMemo(() => {
    const set = new Set(
      tableDayData.map((r) => (r.ordering_party ?? "").trim()).filter(Boolean),
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [tableDayData]);

  const dayDataUsualGpOptions = React.useMemo(() => {
    const set = new Set(
      tableDayData.map((r) => (r.usual_gp ?? "").trim()).filter(Boolean),
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [tableDayData]);

  const filteredTableDayData = React.useMemo(() => {
    const {
      nhs_num,
      emis_id,
      lab_report_id,
      categories,
      ordering_parties,
      usual_gps,
      statuses,
      category_type,
      report_has_review,
    } = dayDataFilters;

    return tableDayData.filter((row) => {
      if (
        nhs_num &&
        !String(row.nhs_num ?? "")
          .toLowerCase()
          .includes(nhs_num.toLowerCase())
      )
        return false;

      if (
        emis_id &&
        !String(row.emis_id ?? "")
          .toLowerCase()
          .includes(emis_id.toLowerCase())
      )
        return false;

      if (
        lab_report_id &&
        !String(row.lab_report_id ?? "")
          .toLowerCase()
          .includes(lab_report_id.toLowerCase())
      )
        return false;

      if (categories.length > 0) {
        const rowCategories = String(row.category ?? "")
          .split(",")
          .map((c) => c.trim());
        if (!rowCategories.some((c) => categories.includes(c))) return false;
      }

      if (
        ordering_parties.length > 0 &&
        !ordering_parties.includes((row.ordering_party ?? "").trim())
      )
        return false;

      if (
        usual_gps.length > 0 &&
        !usual_gps.includes((row.usual_gp ?? "").trim())
      )
        return false;

      if (statuses.length > 0 && !statuses.includes(row.status)) return false;

      if (category_type !== "") {
        const isNormal = String(Boolean(row.category_type));
        if (isNormal !== category_type) {
          return false;
        }
      }

      if (report_has_review !== "") {
        const hasReview = String(Boolean(row.report_has_review));
        if (hasReview !== report_has_review) {
          return false;
        }
      }
      return true;
    });
  }, [tableDayData, dayDataFilters]);

  useEffect(() => {
    setDayDataFilters({
      nhs_num: "",
      emis_id: "",
      lab_report_id: "",
      categories: [],
      ordering_parties: [],
      usual_gps: [],
      statuses: [],
      category_type: "",
      report_has_review: "",
    });
    setSelectedCategoryRowId(null);
  }, [selectedDay]);

  const pageSize = 10;
  const totalPages = Math.ceil(tableTwo.length / pageSize);

  const paginatedData = tableTwo
    .filter((entry) => entry.filed > 0)
    .sort((a, b) => b.filed - a.filed)
    .slice(page * pageSize, (page + 1) * pageSize);

  const paginatedData3 = tableTwo
    .filter((entry) => entry.not_filed_inconclusive > 0)
    .sort((a, b) => b.not_filed_inconclusive - a.not_filed_inconclusive)
    .slice(page3 * pageSize, (page3 + 1) * pageSize);

  const paginatedData4 = tableTwo
    .filter((entry) => entry.not_filed_setup > 0)
    .sort((a, b) => b.not_filed_setup - a.not_filed_setup)
    .slice(page4 * pageSize, (page4 + 1) * pageSize);

  useEffect(() => {
    setSelectedDayRowId(null);
    setSelectedEMISId(null);
    setSelectedCategoryRowId(null);
    setPage(0);
    setPage3(0);
    setPage4(0);
  }, [liverun]);

  useEffect(() => {
    return () => {
      dispatch(resetDashboard());
    };
  }, [dispatch]);

  useEffect(() => {
    if (license_key) {
      dispatch(fetchTableOne({ liverun, license_key, access_token }));
    }
  }, [liverun, license_key, access_token, dispatch]);

  useEffect(() => {
    if (tableOne.length > 0 && license_key && !selectedDate) {
      const lastEntry = tableOne[0];
      const lastDate = lastEntry.date;
      const { start_date, end_date } = lastEntry;
      dispatch(setSelectedDate(lastDate));
      dispatch(
        fetchTableTwo({
          start_date,
          end_date,
          liverun,
          license_key,
          access_token,
        }),
      );
      dispatch(
        fetchTableThree({
          start_date,
          end_date,
          liverun,
          license_key,
          access_token,
        }),
      );
      setPage(0);
      setPage3(0);
      setPage4(0);
    }
  }, [tableOne, license_key, liverun, dispatch, selectedDate, access_token]);

  useEffect(() => {
    if (tableThree.length > 0 && !selectedDay) {
      const lastRow = tableThree[0];
      const dateTitle = lastRow?.output_date;
      if (dateTitle) {
        setSelectedDayRowId(lastRow.id);
        dispatch(setSelectedDay(dateTitle));
        dispatch(
          fetchDayByCategory({
            date: dateTitle,
            liverun,
            license_key,
            access_token,
          }),
        );
        dispatch(
          fetchDayData({ date: dateTitle, liverun, license_key, access_token }),
        );
      }
    }
  }, [tableThree, selectedDay, liverun, license_key, access_token, dispatch]);

  useEffect(() => {
    if (tableThree.length > 0 && selectedDay) {
      const row = tableThree.find((r) => r.output_date === selectedDay);
      if (row) {
        setSelectedDayRowId(row.id);
      }
    }
  }, [tableThree, selectedDay]);

  const handleOpenRangeDialog = () => {
    setRangeError(null);
    setRangeDialogOpen(true);
  };

  const handleCloseRangeDialog = () => {
    if (rangeDownloading) return; // don't let them close mid-request
    setRangeDialogOpen(false);
    setRangeStartDate("");
    setRangeEndDate("");
    setRangeError(null);
  };

  const handleRangeSubmit = () => {
    if (!rangeStartDate || !rangeEndDate) return;

    if (rangeEndDate < rangeStartDate) {
      setRangeError("End date must be on or after start date.");
      return;
    }

    setRangeError(null);
    setRangeDownloading(true);

    dispatch(
      fetchDateRangeExcel({
        start_date: rangeStartDate,
        end_date: rangeEndDate,
        liverun,
        license_key,
        access_token,
      }),
    )
      .unwrap()
      .then(({ blob, filename }) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        handleCloseRangeDialog();
      })
      .catch((error) => {
        setRangeError(
          typeof error === "string" ? error : "Failed to generate the export.",
        );
      })
      .finally(() => {
        setRangeDownloading(false);
      });
  };

  const handleChartClick = (data) => {
    document.activeElement?.blur?.();

    const dateTitle = data?.activeLabel;
    if (!dateTitle) return;
    const matchedRecord = tableOne.find((entry) => entry.date === dateTitle);
    if (!matchedRecord) return;
    const { start_date, end_date } = matchedRecord;
    dispatch(setSelectedDate(dateTitle));
    dispatch(
      fetchTableTwo({
        start_date,
        end_date,
        liverun,
        license_key,
        access_token,
      }),
    );
    dispatch(
      fetchTableThree({
        start_date,
        end_date,
        liverun,
        license_key,
        access_token,
      }),
    );
    dispatch(setSelectedDay(null));
    setSelectedDayRowId(null);
    setPage(0);
    setPage3(0);
    setPage4(0);
    document.activeElement?.blur?.();
  };

  const handleRowSelect = (params) => {
    const dateTitle = params.row?.output_date;
    setSelectedDayRowId(params.row?.id);
    dispatch(setSelectedDay(dateTitle));
    dispatch(
      fetchDayByCategory({
        date: dateTitle,
        liverun,
        license_key,
        access_token,
      }),
    );
    dispatch(
      fetchDayData({ date: dateTitle, liverun, license_key, access_token }),
    );
  };

  const handleCategoryRowSelect = (params) => {
    const category = params.row?.category;
    setSelectedCategoryRowId(params.row?.id);
    setDayDataFilters((prev) => ({
      ...prev,
      categories: category ? [category] : [],
    }));
  };

  const chartFont = { fontSize: 11 };

  const pieData =
    selectedChartType === "filed"
      ? paginatedData
      : selectedChartType === "not_filed_inconclusive"
        ? paginatedData3
        : paginatedData4;

  // Derive a human-readable week range label for the selected date entry
  const selectedWeekLabel = (() => {
    if (!selectedDate || tableOne.length === 0) return "";
    const match = tableOne.find((e) => e.date === selectedDate);
    if (!match) return selectedDate;
    // start_date / end_date are expected to be strings like "2025-06-18"
    const fmt = (d) => {
      if (!d) return "";
      const dt = new Date(d);
      return dt.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    };
    return match.start_date && match.end_date
      ? `${fmt(match.start_date)} – ${fmt(match.end_date)}`
      : selectedDate;
  })();

  const renderLegend = ({ payload }) => {
    const sorted = [...payload].sort(
      (a, b) => b.payload[selectedChartType] - a.payload[selectedChartType],
    );

    return (
      <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 12 }}>
        {sorted.map((entry, index) => (
          <li
            key={`item-${index}`}
            style={{ display: "flex", alignItems: "center", marginBottom: 4 }}
          >
            <div
              style={{
                width: 12,
                height: 12,
                backgroundColor: entry.color,
                marginRight: 8,
              }}
            />
            <span style={{ color: entry.color }}>
              {entry.payload.category} : {entry.payload[selectedChartType]}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  const handleOpenFiling = (params) => {
    const lab_report_id = params.row?.lab_report_id;
    setSelectedEMISId(params.row?.id);
    dispatch(setLabReport(lab_report_id));
    dispatch(fetchFiling({ lab_report_id, liverun, license_key }));
    setOpenFilingDialog(true);
  };

  const handleRowClick = (params) => {
    const lab_report_id = params.row?.lab_report_id;
    setSelectedEMISId(params.row?.id);
  };

  const handleCloseFilingDialog = () => {
    setOpenFilingDialog(false);
  };

  const CustomXAxisTick = ({ x, y, payload }) => {
    const isSelected = payload.value === selectedDate;
    return (
      <g transform={`translate(${x},${y})`}>
        {isSelected && (
          <rect
            x={-28}
            y={-3}
            width={56}
            height={4}
            rx={4}
            fill={DASHBOARDCOLORS.dark_boarder}
            stroke={DASHBOARDCOLORS.dark_boarder}
            strokeWidth={1.5}
          />
        )}
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="end"
          transform="rotate(-45)"
          fontSize={isSelected ? 12 : 11}
          fontWeight={isSelected ? 700 : 400}
          fill={isSelected ? "#b45309" : "#555"}
        >
          {payload.value}
        </text>
      </g>
    );
  };

  return (
    <Box sx={{ mt: 1, pt: 0, pb: 0, px: 2 }}>
      {is_admin && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 0.5,
          }}
        >
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Switch
                size="small"
                checked={liverun}
                onChange={() => dispatch(toggleLiverun())}
                color="success"
              />
            }
            label={
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: liverun ? "#2f855a" : "#c53030" }}
              >
                {liverun ? "Live Run" : "Dry Run"}
              </Typography>
            }
            labelPlacement="start"
          />
        </Box>
      )}
      <Paper elevation={2} sx={{ mt: 1, p: 2, height: "200vh" }}>
        {/* Top Controls */}
        <Box
          sx={{
            background: "#F0F8FF",
            borderRadius: 3,
            p: 1.5,
          }}
        >
          <Grid container spacing={2.5} justifyContent="space-between">
            {[
              {
                value: tableOneTotalNormal,
                label1: "Total Pathology Normal",
                label2: "Reports Filed",
                color: "#faf5ff",
              },
              {
                value: tableOneTotalAbnormal,
                label1: "Total Pathology Abnormal",
                label2: "Reports Filed",
                color: "#fff7e6",
              },
              {
                value:
                  typeof tableOneTotalProcessed === "number"
                    ? Math.ceil(tableOneTotalProcessed / 60)
                    : 0,
                label1: "Hrs Saved",
                label2: "",
                label3: "(based on 1min per filing)",
                caption: true,
                color: "#f7faf7",
              },
              {
                value: tableOneEnabledCategories,
                label1: "Total Pathology",
                label2: "Categories Enabled",
                color: "#fff5f5",
              },
              {
                value: tableOneInreviewCategories,
                label1: "Total Pathology",
                label2: "Categories In Review",
                color: "#f9fafb",
              },
            ].map((card, idx) => (
              <Grid item xs={12} sm={6} md={2} key={idx}>
                <Paper
                  elevation={1}
                  sx={{
                    background: card.color,
                    borderRadius: 2,
                    p: 2,
                    textAlign: "center",
                    height: "50%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    border: "1px solid #e2e8f0",
                    transition: "0.2s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      color: "#2d3748",
                      mb: 1,
                    }}
                  >
                    {typeof card.value === "number"
                      ? card.value.toLocaleString()
                      : card.value}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#4a5568",
                      fontWeight: 500,
                      lineHeight: 1.4,
                    }}
                  >
                    {card.label1}
                    <br />
                    {card.label2}
                  </Typography>

                  {card.caption && (
                    <Typography
                      variant="caption"
                      sx={{ mt: 0.5, color: "#718096" }}
                    >
                      {card.label3}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ))}

            {/* ---------------- MONTHLY TREND CARD ---------------- */}
            <Grid item xs={12} sm={6} md={2}>
              <Paper
                elevation={1}
                sx={{
                  background: "#ffffff",
                  borderRadius: 2,
                  p: 2,
                  height: "75%",
                  border: "1px solid #e2e8f0",
                  transition: "0.2s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "#2d3748",
                    fontWeight: 600,
                    textAlign: "center",
                    mb: 1.5,
                  }}
                >
                  Monthly Trend
                </Typography>

                {Array.isArray(tableOneMonthlyTrend) &&
                  tableOneMonthlyTrend.map((item, idx) => {
                    const isPositive = item.percent_change_from_prev > 0;
                    return (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                          "&:last-child": { mb: 0 },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "#4a5568", fontWeight: 600 }}
                        >
                          {item.month}:
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ color: "#2d3748", fontWeight: 500 }}
                        >
                          {item.filed}
                        </Typography>

                        <Box
                          sx={{
                            px: 1,
                            py: 0.2,
                            borderRadius: 1,
                            backgroundColor: isPositive ? "#c6f6d5" : "#fed7d7",
                            color: isPositive ? "#2f855a" : "#c53030",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.3,
                          }}
                        >
                          {isPositive ? (
                            <TrendingUp size={14} />
                          ) : (
                            <TrendingDown size={14} />
                          )}

                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                            }}
                          >
                            {Math.abs(item.percent_change_from_prev).toFixed(1)}
                            %
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            alignItems: "flex-start",
            mt: 2,
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              p: 1.5,
              borderRadius: 2,
              borderColor: "#cbd5e0",
              background: "#fafafa",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "#2d3748", mb: 0.5 }}
            >
              Weekly Filing Numbers — select week for breakdown
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
              {Object.entries(COLORS).map(
                ([key, color]) =>
                  (key !== "error" || is_admin) && (
                    <FormControlLabel
                      key={key}
                      control={
                        <Switch
                          size="small"
                          checked={visibleKeys[key]}
                          onChange={() =>
                            setVisibleKeys((prev) => ({
                              ...prev,
                              [key]: !prev[key],
                            }))
                          }
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color,
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: color,
                              },
                          }}
                        />
                      }
                      label={
                        <Typography variant="caption" sx={{ color }}>
                          {COLOR_TITLES[key]}
                        </Typography>
                      }
                    />
                  ),
              )}
            </Box>

            {loadingOne ? (
              <CircularProgress />
            ) : (
              <ResponsiveContainer
                width="100%"
                tabIndex={-1}
                height={350}
                style={{ outline: "none" }}
              >
                <BarChart
                  data={tableOne}
                  tabIndex={-1}
                  margin={{ top: 20, right: 30, left: 10, bottom: 5 }}
                  onClick={handleChartClick}
                  style={{ cursor: "pointer", outline: "none" }}
                >
                  <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={<CustomXAxisTick />}
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis style={chartFont} />
                  <Tooltip contentStyle={chartFont} />
                  <Legend
                    wrapperStyle={{
                      paddingTop: "18px",
                      ...chartFont,
                    }}
                  />
                  {visibleKeys.filed && (
                    <Bar
                      dataKey="filed"
                      name="Filed"
                      stackId="a"
                      fill={COLORS.filed}
                      stroke={BCOLORS.filed}
                      strokeWidth={1}
                      shape={(props) => {
                        const isSelected =
                          props.date === selectedDate ||
                          tableOne[props.index]?.date === selectedDate;
                        return (
                          <CustomBar
                            {...props}
                            fill={COLORS.filed}
                            stroke={
                              isSelected
                                ? DASHBOARDCOLORS.dark_boarder
                                : BCOLORS.filed
                            }
                            strokeWidth={isSelected ? 3 : 1}
                            isSelected={isSelected}
                          />
                        );
                      }}
                    ></Bar>
                  )}
                  {visibleKeys.reviewed_and_actioned && (
                    <Bar
                      dataKey="reviewed_and_actioned"
                      name="Out of Range"
                      stackId="a"
                      fill={COLORS.reviewed_and_actioned}
                      stroke={BCOLORS.reviewed_and_actioned}
                      strokeWidth={1}
                      shape={(props) => {
                        const isSelected =
                          props.date === selectedDate ||
                          tableOne[props.index]?.date === selectedDate;
                        return (
                          <CustomBar
                            {...props}
                            fill={COLORS.reviewed_and_actioned}
                            stroke={
                              isSelected
                                ? DASHBOARDCOLORS.dark_boarder
                                : BCOLORS.reviewed_and_actioned
                            }
                            strokeWidth={isSelected ? 3 : 1}
                            isSelected={isSelected}
                          />
                        );
                      }}
                    ></Bar>
                  )}
                  {visibleKeys.not_filed_inconclusive && (
                    <Bar
                      dataKey="not_filed_inconclusive"
                      name="Not Filed (Inconclusive Report)"
                      stackId="a"
                      fill={COLORS.not_filed_inconclusive}
                      stroke={BCOLORS.not_filed_inconclusive}
                      strokeWidth={1}
                      shape={(props) => {
                        const isSelected =
                          props.date === selectedDate ||
                          tableOne[props.index]?.date === selectedDate;
                        return (
                          <CustomBar
                            {...props}
                            fill={COLORS.not_filed_inconclusive}
                            stroke={
                              isSelected
                                ? DASHBOARDCOLORS.dark_boarder
                                : BCOLORS.not_filed_inconclusive
                            }
                            strokeWidth={isSelected ? 3 : 1}
                            isSelected={isSelected}
                          />
                        );
                      }}
                    ></Bar>
                  )}
                  {visibleKeys.not_filed_setup && (
                    <Bar
                      dataKey="not_filed_setup"
                      name="Category Not Setup"
                      stackId="a"
                      fill={COLORS.not_filed_setup}
                      stroke={BCOLORS.not_filed_setup}
                      strokeWidth={1}
                      shape={(props) => {
                        const isSelected =
                          props.date === selectedDate ||
                          tableOne[props.index]?.date === selectedDate;
                        return (
                          <CustomBar
                            {...props}
                            fill={COLORS.not_filed_setup}
                            stroke={
                              isSelected
                                ? DASHBOARDCOLORS.dark_boarder
                                : BCOLORS.not_filed_setup
                            }
                            strokeWidth={isSelected ? 3 : 1}
                            isSelected={isSelected}
                          />
                        );
                      }}
                    ></Bar>
                  )}
                  {visibleKeys.not_filed_already && (
                    <Bar
                      dataKey="not_filed_already"
                      name="Not Filed (Already filed by clinician)"
                      stackId="a"
                      fill={COLORS.not_filed_already}
                      stroke={BCOLORS.not_filed_already}
                      strokeWidth={1}
                      shape={(props) => {
                        const isSelected =
                          props.date === selectedDate ||
                          tableOne[props.index]?.date === selectedDate;
                        return (
                          <CustomBar
                            {...props}
                            fill={COLORS.not_filed_already}
                            stroke={
                              isSelected
                                ? DASHBOARDCOLORS.dark_boarder
                                : BCOLORS.not_filed_already
                            }
                            strokeWidth={isSelected ? 3 : 1}
                            isSelected={isSelected}
                          />
                        );
                      }}
                    ></Bar>
                  )}
                  {visibleKeys.error && is_admin && (
                    <Bar
                      dataKey="error"
                      name="Error"
                      stackId="a"
                      fill={COLORS.error}
                      stroke={BCOLORS.error}
                      strokeWidth={1}
                      shape={(props) => {
                        const isSelected =
                          props.date === selectedDate ||
                          tableOne[props.index]?.date === selectedDate;
                        return (
                          <CustomBar
                            {...props}
                            fill={COLORS.error}
                            stroke={
                              isSelected
                                ? DASHBOARDCOLORS.dark_boarder
                                : BCOLORS.error
                            }
                            strokeWidth={isSelected ? 3 : 1}
                            isSelected={isSelected}
                          />
                        );
                      }}
                    ></Bar>
                  )}
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>

          {/* ---- RIGHT: Pie Chart panel ---- */}
          {selectedDate && !loadingTwo && (
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                p: 1.5,
                borderRadius: 2,
                borderColor: "#cbd5e0",
                background: "#fafafa",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "#2d3748", mb: 0.5 }}
              >
                {selectedWeekLabel
                  ? `Week ${selectedWeekLabel} — Pathology category breakdown`
                  : "Pathology category breakdown"}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <FormControl component="fieldset" sx={{ ml: 0.5 }}>
                  <RadioGroup
                    row
                    value={selectedChartType}
                    onChange={(e) => {
                      setSelectedChartType(e.target.value);
                      setPage(0);
                    }}
                  >
                    <FormControlLabel
                      value="filed"
                      control={
                        <Radio
                          sx={{ transform: "scale(0.8)", p: 0.5 }}
                          size="small"
                        />
                      }
                      label={<Typography variant="caption">Filed</Typography>}
                    />
                    <FormControlLabel
                      value="not_filed_inconclusive"
                      control={
                        <Radio
                          sx={{ transform: "scale(0.8)", p: 0.5 }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption">
                          Not Filed (Inconclusive Report)
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      value="not_filed_setup"
                      control={
                        <Radio
                          sx={{ transform: "scale(0.8)", p: 0.5 }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption">
                          Category Not Setup
                        </Typography>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              <ResponsiveContainer
                width="100%"
                height={350}
                style={{ outline: "none" }}
              >
                <PieChart>
                  <Tooltip contentStyle={chartFont} />
                  <Legend
                    layout="vertical"
                    verticalAlign="middle"
                    align="left"
                    content={renderLegend}
                    wrapperStyle={{
                      ...chartFont,
                      lineHeight: "24px",
                      position: "absolute",
                      left: 0,
                      top: 0,
                    }}
                  />
                  <Pie
                    data={pieData}
                    dataKey={selectedChartType}
                    nameKey="category"
                    outerRadius={100}
                    innerRadius={60}
                    paddingAngle={2}
                    label
                    stroke="#fff"
                    strokeWidth={1}
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={PIE_COLORS[index % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              {totalPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 2,
                    mt: -5,
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={
                      selectedChartType === "filed"
                        ? page === 0
                        : selectedChartType === "not_filed_inconclusive"
                          ? page3 === 0
                          : page4 === 0
                    }
                    onClick={() => {
                      selectedChartType === "filed"
                        ? setPage((p) => p - 1)
                        : selectedChartType === "not_filed_inconclusive"
                          ? setPage3((p) => p - 1)
                          : setPage4((p) => p - 1);
                    }}
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={
                      selectedChartType === "filed"
                        ? page >= totalPages - 1
                        : selectedChartType === "not_filed_inconclusive"
                          ? page3 >= totalPages - 1
                          : page4 >= totalPages - 1
                    }
                    onClick={() => {
                      selectedChartType === "filed"
                        ? setPage((p) => p + 1)
                        : selectedChartType === "not_filed_inconclusive"
                          ? setPage3((p) => p + 1)
                          : setPage4((p) => p + 1);
                    }}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </Paper>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mt: 3 }}>
          {selectedDate && !loadingThree && (
            <DataGrid
              rowHeight={25}
              rows={tableThree}
              columns={TABLE_THREE_COLUMNS}
              pagination={false}
              hideFooter={true}
              onRowClick={handleRowSelect}
              getRowClassName={(params) => {
                const isSelectedRow = params.row.id === selectedDayRowId;
                if (isSelectedRow) return "selected-day-row";
                return params.indexRelativeToCurrentPage % 2 === 0
                  ? "even"
                  : "odd";
              }}
              slots={{
                columnMenu: CustomColumnMenu,
              }}

              columnHeaderHeight={40}
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  minHeight: "40px !important",
                  maxHeight: "40px !important",
                  lineHeight: "40px !important",
                },

                "& .MuiDataGrid-columnHeader": {
                  paddingTop: "4px",
                  paddingBottom: "4px",
                },

                "& .MuiDataGrid-columnHeaderTitle": {
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                width: "100%",
                "& .MuiDataGrid-columnHeaders": {
                  marginBottom: "16px",
                },
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                },
                "& .selected-day-row": {
                  backgroundColor: "#fef9c3 !important",
                  outline: "2px solid #7c6fe0",
                  outlineOffset: "-2px",
                  fontWeight: 700,
                },
                "& .selected-day-row:hover": {
                  backgroundColor: "#fef08a !important",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                  {
                    outline: "none",
                  },
              }}
            />
          )}
        </Box>

        {selectedDay && (
          <>
            <Box sx={{ mt: 3, height: 310, width: "100%" }}>
              <Typography variant="subtitle1" gutterBottom>
                Category Data for {selectedDay}
              </Typography>
              {loadingDayByCategory ? (
                <CircularProgress />
              ) : (
                <DataGrid
                  rowHeight={25}
                  rows={tableDayByCategory}
                  columns={TABLE_CATEGORY_COLUMNS}
                  onRowClick={handleCategoryRowSelect}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 100,
                      },
                    },
                  }}
                  getRowClassName={(params) => {
                    if (params.row.id === selectedCategoryRowId)
                      return "selected-day-row";
                    return params.indexRelativeToCurrentPage % 2 === 0
                      ? "even"
                      : "odd";
                  }}
                  slots={{
                    columnMenu: CustomColumnMenu,
                  }}
                  columnHeaderHeight={40}
                  sx={{
                    "& .MuiDataGrid-columnHeaders": {
                      minHeight: "40px !important",
                      maxHeight: "40px !important",
                      lineHeight: "40px !important",
                    },

                    "& .MuiDataGrid-columnHeader": {
                      paddingTop: "4px",
                      paddingBottom: "4px",
                    },

                    "& .MuiDataGrid-columnHeaderTitle": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                    width: "100%",
                    "& .MuiDataGrid-columnHeaders": {
                      marginBottom: "16px",
                    },

                    "& .MuiDataGrid-row": {
                      cursor: "pointer",
                    },
                    "& .selected-day-row": {
                      backgroundColor: "#fef9c3 !important",
                      outline: "2px solid #7c6fe0",
                      outlineOffset: "-2px",
                      fontWeight: 700,
                    },
                    "& .selected-day-row:hover": {
                      backgroundColor: "#fef08a !important",
                    },
                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                      {
                        outline: "none",
                      },
                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                      {
                        outline: "none",
                      },
                  }}
                />
              )}
            </Box>

            <Box sx={{ mt: 1, height: 310, width: "100%" }}>
              <Typography variant="subtitle1" gutterBottom>
                Day Data for {selectedDay}
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 1.5,
                  borderRadius: 2,
                  borderColor: "#cbd5e0",
                  background: "#fafafa",
                }}
              >
                <Grid container spacing={1.5} alignItems="flex-start">
                  <Grid item xs={12} sm={6} md={2.5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search NHS Number"
                      value={dayDataFilters.nhs_num}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        setDayDataFilters((prev) => ({
                          ...prev,
                          nhs_num: value,
                        }));
                      }}
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search EMIS ID"
                      value={dayDataFilters.emis_id}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        setDayDataFilters((prev) => ({
                          ...prev,
                          emis_id: value,
                        }));
                      }}
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search Lab Report Id"
                      value={dayDataFilters.lab_report_id}
                      onChange={(e) =>
                        setDayDataFilters((prev) => ({
                          ...prev,
                          lab_report_id: e.target.value,
                        }))
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Autocomplete
                      multiple
                      size="small"
                      sx={{ minWidth: 400 }}
                      options={dayDataCategoryOptions}
                      getOptionLabel={(option) => option.label || ""}
                      filterOptions={(options, state) => {
                        const input = (state.inputValue || "").toLowerCase();
                        return options.filter((option) =>
                          (option.label || "").toLowerCase().includes(input),
                        );
                      }}
                      value={dayDataCategoryOptions.filter((opt) =>
                        dayDataFilters.categories.includes(opt.value),
                      )}
                      onChange={(event, newValue) => {
                        setDayDataFilters((prev) => ({
                          ...prev,
                          categories: newValue.map((v) => v.value),
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Search Category" />
                      )}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      multiple
                      size="small"
                      sx={{ minWidth: 200 }}
                      options={dayDataOrderingPartyOptions}
                      value={dayDataFilters.ordering_parties}
                      onChange={(event, newValue) => {
                        setDayDataFilters((prev) => ({
                          ...prev,
                          ordering_parties: newValue,
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Search Ordering Party" />
                      )}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      multiple
                      size="small"
                      sx={{ minWidth: 200 }}

                      options={dayDataUsualGpOptions}
                      value={dayDataFilters.usual_gps}
                      onChange={(event, newValue) => {
                        setDayDataFilters((prev) => ({
                          ...prev,
                          usual_gps: newValue,
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Search Usual GP" />
                      )}
                      fullWidth
                    />
                  </Grid>

                  <Grid container spacing={1.5} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={4}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#4a5568",
                          fontWeight: 600,
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        Filter by Report Type
                      </Typography>

                      <RadioGroup
                        row
                        value={dayDataFilters.category_type}
                        onChange={(e) =>
                          setDayDataFilters((prev) => ({
                            ...prev,
                            category_type: e.target.value,
                          }))
                        }
                      >
                        <FormControlLabel
                          value=""
                          control={<Radio size="small" />}
                          label={<Typography variant="caption">All</Typography>}
                        />
                        <FormControlLabel
                          value="true"
                          control={<Radio size="small" />}
                          label={
                            <Typography variant="caption">
                              Normal Report
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value="false"
                          control={<Radio size="small" />}
                          label={
                            <Typography variant="caption">
                              Abnormal Report
                            </Typography>
                          }
                        />
                      </RadioGroup>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#4a5568",
                          fontWeight: 600,
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        Filter by Review Approved Records
                      </Typography>

                      <RadioGroup
                        row
                        value={dayDataFilters.report_has_review}
                        onChange={(e) =>
                          setDayDataFilters((prev) => ({
                            ...prev,
                            report_has_review: e.target.value,
                          }))
                        }
                      >
                        <FormControlLabel
                          value=""
                          control={<Radio size="small" />}
                          label={<Typography variant="caption">All</Typography>}
                        />
                        <FormControlLabel
                          value="true"
                          control={<Radio size="small" />}
                          label={<Typography variant="caption">On</Typography>}
                        />
                        <FormControlLabel
                          value="false"
                          control={<Radio size="small" />}
                          label={<Typography variant="caption">Off</Typography>}
                        />
                      </RadioGroup>
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#4a5568",
                          fontWeight: 600,
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        Filter by Status
                      </Typography>
                      <FormGroup row>
                        {dayDataStatusOptions.map((statusValue) => (
                          <FormControlLabel
                            key={statusValue}
                            control={
                              <Checkbox
                                size="small"
                                checked={dayDataFilters.statuses.includes(
                                  statusValue,
                                )}
                                onChange={(e) => {
                                  setDayDataFilters((prev) => {
                                    const next = e.target.checked
                                      ? [...prev.statuses, statusValue]
                                      : prev.statuses.filter(
                                          (s) => s !== statusValue,
                                        );
                                    return { ...prev, statuses: next };
                                  });
                                }}
                              />
                            }
                            label={
                              <Typography variant="caption">
                                {statusValue}
                              </Typography>
                            }
                          />
                        ))}
                      </FormGroup>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={handleOpenRangeDialog}
                        sx={{ mt: { xs: 0, md: 0.5 } }}
                      >
                        Export Date Range
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>

              <Dialog
                open={rangeDialogOpen}
                onClose={handleCloseRangeDialog}
                fullWidth
                maxWidth="xs"
              >
                <DialogTitle>Export Blood Filings by Date Range</DialogTitle>
                <DialogContent>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{ mt: 1 }}
                  >
                    <TextField
                      label="Start Date"
                      type="date"
                      value={rangeStartDate}
                      onChange={(e) => setRangeStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      value={rangeEndDate}
                      onChange={(e) => setRangeEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    {rangeError && (
                      <Typography color="error" variant="body2">
                        {rangeError}
                      </Typography>
                    )}
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={handleCloseRangeDialog}
                    disabled={rangeDownloading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleRangeSubmit}
                    disabled={
                      !rangeStartDate || !rangeEndDate || rangeDownloading
                    }
                  >
                    {rangeDownloading ? (
                      <CircularProgress size={20} />
                    ) : (
                      "Download Excel"
                    )}
                  </Button>
                </DialogActions>
              </Dialog>

              {loadingDayData ? (
                <CircularProgress />
              ) : (
                <DataGrid
                  rowHeight={25}
                  rows={filteredTableDayData}
                  columns={TABLE_DAYDATA_COLUMNS}
                  onRowDoubleClick={handleOpenFiling}
                  onRowClick={handleRowClick}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 100,
                      },
                    },
                  }}
                  getRowClassName={(params) => {
                    const isSelectedRow = params.row.id === selectedEMISId;
                    if (isSelectedRow) return "selected-day-row";
                    return params.indexRelativeToCurrentPage % 2 === 0
                      ? "even"
                      : "odd";
                  }}

                  slots={{
                    columnMenu: CustomColumnMenu,
                  }}
                  columnHeaderHeight={44}
                  sx={{
                    "& .MuiDataGrid-columnHeaders": {
                      minHeight: "44px !important",
                      maxHeight: "44px !important",
                      lineHeight: "44px !important",
                    },

                    "& .MuiDataGrid-columnHeader": {
                      paddingTop: "4px",
                      paddingBottom: "4px",
                    },

                    "& .MuiDataGrid-columnHeaderTitle": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },

                    width: "100%",
                    "& .MuiDataGrid-columnHeaders": {
                      marginBottom: "16px",
                    },

                    "& .MuiDataGrid-row": {
                      cursor: "pointer",
                    },
                    "& .selected-day-row": {
                      backgroundColor: "#fef9c3 !important",
                      outline: "2px solid #7c6fe0",
                      outlineOffset: "-2px",
                      fontWeight: 700,
                    },
                    "& .selected-day-row:hover": {
                      backgroundColor: "#fef08a !important",
                    },
                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                      {
                        outline: "none",
                      },
                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                      {
                        outline: "none",
                      },
                  }}
                />
              )}
            </Box>
          </>
        )}

        <Dialog
          open={openFilingDialog}
          onClose={handleCloseFilingDialog}
          maxWidth="xl"
          fullWidth
          scroll="paper"
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>
            Filing Details
            <Button
              onClick={handleCloseFilingDialog}
              sx={{ float: "right" }}
              color="error"
              variant="outlined"
            >
              Close
            </Button>
          </DialogTitle>

          <DialogContent dividers>
            {loadingLabReport ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading filing...</Typography>
              </Box>
            ) : lab && Object.keys(lab).length > 0 ? (
              <>
                <LabFilingDetails lab={lab} />
              </>
            ) : null}
          </DialogContent>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default DashboardFrame;
